import { useState } from "react";

import {
  buildSettlementAmountPayload,
  buildSettlementAmountValues,
  isSettlementAmountDirty,
  normalizeNullableCurrencyInput,
} from "../helper/caseForms";
import { toDateInputValue } from "../helper/date";

import useSettlementCaseForm from "./useSettlementCaseForm";
import useSettlementPermissions from "./useSettlementPermissions";

export default function useSettlementAmountSection({ caseDetail }) {
  const { canViewPayments } = useSettlementPermissions();
  const {
    formValues,
    setFormValues,
    isDirty,
    isEditing,
    setIsEditing,
    isSaving,
    canEditCaseSettlement,
    handleCancel: handleBaseCancel,
    handleSave: handleBaseSave,
  } = useSettlementCaseForm({
    caseDetail,
    buildValues: buildSettlementAmountValues,
    buildPayload: buildSettlementAmountPayload,
    isDirtyCheck: isSettlementAmountDirty,
  });
  const [isAmountEditing, setIsAmountEditing] = useState(false);
  const hasSettlementAmount = Boolean(
    caseDetail?.actualTotalSettlementAmount ?? caseDetail?.actual_total_settlement_amount
  );
  const hasCheck = Boolean(caseDetail?.hasCheck ?? caseDetail?.has_check);
  const showStatus = canViewPayments && (hasSettlementAmount || hasCheck);

  const handleFieldChange = (fieldName, value) => {
    if (!canEditCaseSettlement) {
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "actualTotalSettlementAmount"
          ? normalizeNullableCurrencyInput(value)
          : toDateInputValue(value),
    }));
  };

  const handleCancel = () => {
    handleBaseCancel();
    setIsAmountEditing(false);
  };

  const handleSave = async () => {
    await handleBaseSave(() => {
      setIsAmountEditing(false);
    });
  };

  const handleBeginEdit = () => {
    if (!canEditCaseSettlement) {
      return;
    }

    setIsEditing(true);
  };

  const handleBeginAmountEdit = () => {
    if (!canEditCaseSettlement) {
      return;
    }

    setIsEditing(true);
    setIsAmountEditing(true);
  };

  const handleClearAmount = () => {
    if (!canEditCaseSettlement) {
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      actualTotalSettlementAmount: "",
    }));
  };

  return {
    formValues,
    isDirty,
    isEditing,
    isAmountEditing,
    isSaving,
    hasCheck,
    showStatus,
    canEditCaseSettlement,
    handleBeginEdit,
    handleBeginAmountEdit,
    handleClearAmount,
    handleFieldChange,
    handleCancel,
    handleSave,
  };
}
